import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    LabelList,
    Legend,
} from 'recharts';

const CustomBarLabel = ({ x, y, width, value }) => {
    return (
        <text
            x={x + width - 8}
            y={y + 11}
            fill="#333"
            textAnchor="end"
            fontSize={10}
            fontWeight={600}
        >
            {value}%
        </text>
    );
};

const SimpleBarChart = ({ graphData, view }) => {
    const CustomGroupLabel = ({ x, y, width, height, name, ...props }) => {
        const payload = graphData?.find((item, index) => index === props.index) || null;
        if (!payload) return null;

        return (
            <text
                x={x}
                y={y + height + 10}
                fill="#666"
                fontSize={10}
                textAnchor="start"
                color='#667F93'
                fontFamily="Unilever Shilling"
                fontWeight={500}
            >
                NL: {payload["Net lines"]}&nbsp;|&nbsp; IS: {payload["In stock lines"]}&nbsp; |&nbsp; LF: {payload["In stock LF"]}%
            </text>
        );
    };

    const DeltaLabel = ({ x, y, width, height, ...props }) => {
        const payload = graphData?.find((item, index) => index === props.index) || null;
        if (!payload) return null;
        const isPositive = payload["Net improvement"] >= 0;

        const pillWidth = 500;
        const rightPadding = 30;

        return (
            <g transform={`translate(${props.viewBox.x + pillWidth + rightPadding}, ${y + height / 2})`}>
                <rect
                    x={-25}
                    y={-9}
                    rx={10}
                    ry={10}
                    width={50}
                    height={20}
                    fill={isPositive ? '#E8F8EC' : '#FDECEC'}
                />
                <text
                    x={0}
                    y={5}
                    textAnchor="middle"
                    fontSize={10}
                    fontWeight={600}
                    fill={isPositive ? '#2E7D32' : '#C62828'}
                >
                    {isPositive ? '↗' : '↘'} {Math.abs( payload["Net improvement"])}%
                </text>
            </g>
        );
    };

    const CustomLegend = () => {
    const items = [
        { label: 'First Expiry', color: '#FFDCC5' },
        { label: 'OOS Lines', color: '#CCF4C7' },
    ];

    return (
        <div style={{ display: 'flex', gap: 16, fontSize: 12, justifyContent: 'center', marginTop: 10 }}>
            {items.map(item => (
                <div
                    key={item.label}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                    }}
                >
                    <span
                        style={{
                            width: 12,
                            height: 12,
                            borderRadius: 4,
                            backgroundColor: item.color,
                            display: 'inline-block',
                        }}
                    />
                    <span>{item.label}</span>
                </div>
            ))}
        </div>
    );
};

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div
                    style={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        minWidth: '200px',
                    }}
                >
                    <p
                        style={{
                            margin: '0 0 8px 0',
                            fontWeight: 600,
                            fontSize: '12px',
                            color: '#333',
                            borderBottom: '1px solid #eee',
                            paddingBottom: '6px',
                        }}
                    >
                        {label}
                    </p>
                    {payload.map((entry, index) => (
                        <div
                            key={`item-${index}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                margin: '6px 0',
                                fontSize: '11px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span
                                    style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '2px',
                                        backgroundColor: entry.color,
                                        display: 'inline-block',
                                    }}
                                />
                                <span style={{ color: '#666' }}>{entry.name}:</span>
                            </div>
                            <span style={{ fontWeight: 600, color: '#333', marginLeft: '8px' }}>
                                {entry.value}%
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    const barGroupHeight = 60; // Height per bar group (includes both bars + spacing)
    const minHeight = 200; // Minimum container height
    const maxHeight = 400; // Maximum visible height before scroll
    const calculatedHeight = Math.max(minHeight, (graphData?.length || 0) * barGroupHeight);
    const containerHeight = Math.min(maxHeight, calculatedHeight);

    return (
        <div style={{ 
            width: '100%', 
            height: containerHeight, 
            overflowY: calculatedHeight > maxHeight ? 'auto' : 'visible',
            overflowX: 'hidden'
        }}>
            <div style={{ width: '100%', height: calculatedHeight }}>
                <ResponsiveContainer width="100%" height={calculatedHeight}>
                    <BarChart
                        data={graphData}
                        layout="vertical"
                        barGap={0}
                        barCategoryGap={10}
                        margin={{ top: 10, right: 40, left: 40, bottom: 0 }}
                    >
                        <XAxis
                            type="number"
                            domain={[0, 100]}
                            tickFormatter={(v) => `${v}%`}
                            hide={true}
                        />
                        <YAxis
                            type="category"
                            dataKey={view === 'Goodness Improvement' ? 'BG' : 'DC'}
                            width={80}
                            tick={{ fontSize: 10, fontWeight: 600 }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />

                        {/* First Expiry */}
                        <Bar
                            dataKey="First expiry"
                            fill="#FFDCC5"
                            radius={[0, 6, 6, 0]}
                            barSize={14}
                        >
                            <LabelList content={<CustomBarLabel />} />
                        </Bar>

                        {/* OOS Lines */}
                        <Bar
                            dataKey="OOS Lines"
                            fill="#CCF4C7"
                            radius={[0, 6, 6, 0]}
                            barSize={14}
                        >
                            <LabelList content={<CustomBarLabel />} />
                            <LabelList content={<CustomGroupLabel />} />
                            <LabelList content={<DeltaLabel />} />
                        </Bar>
                        <Legend
                            verticalAlign="bottom"
                            align="centre"
                            content={<CustomLegend />}
                            margin={10}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}

export default SimpleBarChart;